import { Box, Skeleton } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { ReactNode } from 'react';
import { TFunction } from 'i18next';

import {
  RowLayout,
  ColumnLayout,
  Typography,
  Button,
} from '@/components/atoms';

interface HeaderConfigParams {
  theme: Theme;
  logo: ReactNode;
  loanOfficer: string;
  nmls: string;
  showLogin?: boolean;
  displayName?: string; // Override display name (e.g., company name)
  loanOfficerPhoto?: string;
  isProfileLogo?: boolean; // True when using profile_logo.png (no background needed)
  variant?: 'landing' | 'application'; // 'landing' = rounded without profile, 'application' = rectangular with profile
  t?: TFunction; // Translation function
  isPhotoLoading?: boolean; // Show loading skeleton for photo
  isLoanOfficerSelected?: boolean; // True when a loan officer is selected (shows 'Loan Officer, NMLS'), false for company (shows just 'NMLS')
  onContactClick?: () => void; // Callback when Contact button is clicked
}

export const getHeaderConfig = ({
  theme,
  logo,
  loanOfficer,
  nmls,
  showLogin = false,
  displayName,
  loanOfficerPhoto,
  isProfileLogo = false,
  variant = 'landing',
  t,
  isPhotoLoading = false,
  isLoanOfficerSelected = true,
  onContactClick,
}: HeaderConfigParams) => {
  const isApplicationVariant = variant === 'application';

  // Contact button component (consistent styling)
  const ContactButton = ({ isSmall = false }: { isSmall?: boolean }) => (
    <Button
      variant="outlined"
      disableRipple
      onClick={onContactClick}
      sx={{
        ...(isSmall
          ? theme.typography.M3.label.mediumProminent
          : theme.typography.M3.label.large),
        color: theme.palette.M3?.sys?.light?.primary,
        borderRadius: isSmall ? theme.unit(1.5) : theme.unit(3),
        textTransform: 'none',
        paddingX: isSmall ? theme.unit(2.5) : theme.unit(6),
        paddingY: isSmall ? theme.unit(1) : theme.unit(2.5),
        width: isSmall ? '77px' : '160px',
        height: isSmall ? '28px' : '40px',
        minHeight: isSmall ? '28px' : '40px',
        border: `${theme.unit(0.25)} solid ${theme.palette.M3?.sys?.light?.primary}`, // 1px
        transition:
          'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
        '&:hover': {
          border: `${theme.unit(0.25)} solid ${theme.palette.M3?.sys?.light?.primary}`, // 1px
          backgroundColor: 'transparent',
        },
        '&:focus': {
          backgroundColor: 'transparent',
        },
        '&:active': {
          backgroundColor: 'transparent',
        },
      }}
    >
      Contact
    </Button>
  );

  // Loan Officer info with photo (for desktop/tablet views)
  const LoanOfficerInfo = () => (
    <RowLayout alignItems="center" sx={{ gap: theme.unit(2.5) }}>
      {/* Loan Officer Photo */}
      <Box
        sx={{
          width: '48px',
          height: '48px',
          borderRadius: theme.unit(25), // 100px
          border: `${theme.unit(0.1)} solid ${theme.palette.M3?.sys?.light?.['outline 3']}`, // 0.4px
          backgroundColor: isProfileLogo
            ? 'transparent'
            : theme.palette['Color Selection']?.Teal?.['Teal 200'],
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {isPhotoLoading ? (
          <Skeleton
            variant="circular"
            width={48}
            height={48}
            animation="wave"
          />
        ) : (
          loanOfficerPhoto && (
            <Box
              component="img"
              src={loanOfficerPhoto}
              alt="Loan Officer"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          )
        )}
      </Box>
      {/* Loan Officer Name and NMLS */}
      <ColumnLayout sx={{ gap: theme.unit(1) }}>
        <Typography
          sx={{
            ...theme.typography.M3.body.mediumProminent,
            color: theme.palette.M3?.sys?.light?.['on-surface'],
          }}
        >
          {displayName || loanOfficer}
        </Typography>
        <Typography
          sx={{
            ...theme.typography.M3.body.mediumProminent,
            color: theme.palette.M3?.sys?.light?.['on-surface-variant'],
          }}
        >
          {isLoanOfficerSelected
            ? t
              ? t('header.loanOfficerNmls', { nmls })
              : `Loan Officer, NMLS ${nmls}`
            : t
              ? t('header.companyNmls', { nmls })
              : `NMLS ${nmls}`}
        </Typography>
      </ColumnLayout>
    </RowLayout>
  );

  return {
    position: 'static' as const,
    elevation: 0,
    logo: (
      <Box
        component="img"
        src={logo as string}
        alt="Quick Loan Logo"
        sx={{
          // Desktop/Tablet: 95x40, Mobile: 79x33
          width: { xs: '79px', md: '95px' },
          height: { xs: '33px', md: '40px' },
        }}
      />
    ),
    actions: showLogin ? (
      // Login state - shows Login button only
      <Button
        variant="outlined"
        disableRipple
        sx={{
          ...theme.typography.M3.label.large,
          color: theme.palette.M3?.sys?.light?.primary,
          borderRadius: theme.unit(3),
          textTransform: 'none',
          paddingX: theme.unit(6),
          paddingY: theme.unit(2.5),
          width: '160px',
          height: '40px',
          border: `${theme.unit(0.25)} solid ${theme.palette.M3?.sys?.light?.primary}`, // 1px
          transition:
            'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
          '&:hover': {
            border: `${theme.unit(0.25)} solid ${theme.palette.M3?.sys?.light?.primary}`, // 1px
            backgroundColor: 'transparent',
          },
          '&:focus': {
            backgroundColor: 'transparent',
          },
          '&:active': {
            backgroundColor: 'transparent',
          },
        }}
      >
        Log In
      </Button>
    ) : (
      // Logged in state with loan officer info
      <>
        {/* Desktop/Tablet Layout (md and up) - Show full LO info */}
        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
          <RowLayout alignItems="center" sx={{ gap: theme.unit(6) }}>
            <LoanOfficerInfo />
            <ContactButton />
          </RowLayout>
        </Box>

        {/* Mobile Layout (xs to md) - Hide LO info, show compact controls */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <RowLayout alignItems="center" sx={{ gap: theme.unit(5) }}>
            {/* Small LO photo on mobile */}
            <Box
              sx={{
                width: '36px',
                height: '36px',
                borderRadius: theme.unit(25), // 100px
                border: `${theme.unit(0.1)} solid ${theme.palette.M3?.sys?.light?.['outline 3']}`, // 0.4px
                backgroundColor: isProfileLogo
                  ? 'transparent'
                  : theme.palette['Color Selection']?.Teal?.['Teal 200'],
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {isPhotoLoading ? (
                <Skeleton
                  variant="circular"
                  width={36}
                  height={36}
                  animation="wave"
                />
              ) : (
                loanOfficerPhoto && (
                  <Box
                    component="img"
                    src={loanOfficerPhoto}
                    alt="Loan Officer"
                    sx={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )
              )}
            </Box>
            <ContactButton isSmall />
          </RowLayout>
        </Box>
      </>
    ),
    sx: {
      // Responsive padding: Desktop 24px, Mobile 16px
      paddingX: { xs: theme.unit(4), md: theme.unit(6) },
      paddingY: { xs: theme.unit(4), md: theme.unit(6) },
      border: `${theme.unit(0.25)} solid ${theme.palette.M3?.sys?.light?.['outline 3']}`, // 1px
      borderWidth: `0 0 ${theme.unit(0.25)} 0`,
      backgroundColor: theme.palette.M3?.sys?.light?.background,
      boxShadow: 'none',
      // No margin bottom for application variant (it's flush with content)
      marginBottom: isApplicationVariant ? 0 : theme.unit(10),
      '& .MuiToolbar-root': {
        padding: 0,
        minHeight: 'auto',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
    },
    children: (
      <>
        {/* Spacer to push actions to the right */}
        <Box sx={{ flexGrow: 1 }} />
      </>
    ) as ReactNode,
  };
};
